import React, {useMemo, useState} from 'react';
import {Button, Stack, Figure, ListGroup, Alert,} from "react-bootstrap";
import {Link, useNavigate, useParams} from "react-router-dom";
import {ButtonDelete} from "../../../_components/DeleteButton";
import {useAlert, useAuth} from "../../../../context";
import {FileUpload, OurAlert, OurClearAlert, PageTitle, SingleText} from "../../../_components";
import {isoTimestampToReadableDate} from "../utilities/readableDate";
import {config} from "../../../../utils2";
import {getHumanReadableBytes} from "../utilities/convertBytes";
import {useInfiniteScroll, useMedia, useTagCount, useTagList, useTruncatedText} from "../../../../hooks";
import {DynamicBox, DynamicDialog} from "../../../_components/BoxGroupLayout";
import {Formik, Form as FormikForm, useFormikContext, FieldArray} from "formik";
import {WhiteCard} from "../../../device/_components/WhiteCard";


export function AddEditMedia() {
    const {mid} = useParams()
    const {userId} = useAuth()
    const {success} = useAlert()
    const navigate = useNavigate()

    const {
        value,
        validationSchema,
        addItem,
        editItem,
        deleteItem,
        errorValue,
        clearErrorValue,
    } = useMedia(userId, mid)

    const edit = mid !== "add"

    function handleSubmit(values, actions) {
        if (edit) {
            editItem(values)
                .then(msg => {
                    success(msg)
                    navigate(-1)
                })
                .catch(console.warn)
                .finally(() => actions.setSubmitting(false))
        } else {
            addItem(values)
                .then(msg => {
                    success(msg)
                    navigate(-1)
                })
                .catch(console.warn)
                .finally(() => actions.setSubmitting(false))
        }
    }


    function handleOnDelete() {
        deleteItem()
            .then(msg => {
                success(msg)
                navigate(-1)
            })
            .catch(console.warn)
    }


    return (
        <>
            <OurClearAlert variant="danger" msg={errorValue} onClear={clearErrorValue}/>

            <PageTitle title="Media Details">{edit ? "Edit" : "Add"} media.</PageTitle>

            {/*<FieldInputs value={value} invalidValue={invalidValue} updateFields={setValue} edit={edit}></FieldInputs>*/}
            <Formik validationSchema={validationSchema} onSubmit={handleSubmit} initialValues={value} enableReinitialize={true}>
                {({isSubmitting, resetForm}) => (
                    <FormikForm>

                        <RenderFigures edit={edit}/>

                        <WhiteCard>
                            <FileUpload name="upload_white" label="File (white background)"/>
                            <FileUpload name="upload_black" label="File (black background)" className="my-2"/>

                            <SingleText name="description" label="Description" maxLength="128" className="my-2"/>
                        </WhiteCard>

                        <WhiteCard>
                            <FieldArray name="tags" component={LinkedTags}/>
                        </WhiteCard>

                        <Stack className="my-2 text-secondary">
                            {value.created_at && <span>Created at: {isoTimestampToReadableDate(value.created_at)}</span>}
                            {value.updated_at && <span>Updated at: {isoTimestampToReadableDate(value.updated_at)}</span>}
                        </Stack>

                        <Stack direction="horizontal" gap="2" className="py-3 justify-content-end">
                            <Button as={Link} variant="link" to={-1}>Go back</Button>
                            <Button type="reset" variant="secondary" onClick={resetForm}>Reset</Button>
                            {edit && <ButtonDelete variant="danger" onDelete={handleOnDelete} value={value.name}>Delete</ButtonDelete>}
                            <Button type="submit" variant="success" disabled={isSubmitting}>{edit ? "Edit" : "Add"}</Button>
                        </Stack>

                    </FormikForm>
                )}
            </Formik>
        </>
    );
}

function RenderFigures({edit}) {
    const {values} = useFormikContext()

    const media_white = values?.upload_white
    const media_black = values?.upload_black

    const media_src_white = media_white ? URL.createObjectURL(media_white) : config.get_public_media(values.path, "thumb", "white", true)
    const media_src_black = media_black ? URL.createObjectURL(media_black) : config.get_public_media(values.path, "thumb", "black", true)

    const desc_white = values?.description || "Picture with white background"
    const desc_black = values?.description || "Picture with black background"

    return <>
        <RenderFigure edit={edit} media_src={media_src_white} size={values?.file_size_white} description={desc_white}
                      href={config.get_public_media(values.path, "big", "white")}/>
        <RenderFigure edit={edit} media_src={media_src_black} size={values?.file_size_black} description={desc_black}
                      href={config.get_public_media(values.path, "big", "black")}/>
    </>
}

function RenderFigure({edit, media_src, size, description, href}) {
    const [showFigErr, setShowFigErr] = useState(false)

    return <>
        <OurAlert variant="danger" show={showFigErr} title="Image not found!">
            <Alert.Link href={media_src} target="_blank" rel="noopener noreferrer">{media_src}!</Alert.Link>
        </OurAlert>

        {(!showFigErr && media_src) &&
            <div className="d-flex justify-content-center">
                <Figure className="text-center">
                    <Figure.Image
                        src={media_src}
                        alt={description}
                        // height="650px"
                        // width="650px"
                        onError={() => setShowFigErr(true)}
                    />
                    {edit && <Figure.Caption>
                        {/*<span>Media path: <a href={href} target="_blank" rel="noopener noreferrer">{useTruncatedText(href, 25, false)}</a></span>*/}
                        <span>Media path: <a href={href} target="_blank" rel="noopener noreferrer">{href}</a></span>
                        <br/>
                        <span>File size: {getHumanReadableBytes(size)}</span>
                    </Figure.Caption>}
                </Figure>
            </div>
        }
    </>
}


function LinkedTags({push, remove, form}) {
    const [show, setShow] = useState(false)
    const [filter, setFilter] = useState("")
    const [pageIndex, setPageIndex] = useState(0)

    const pageSize = 10

    const {count} = useTagCount()
    const {list, loadingList} = useTagList(pageSize, pageIndex, null, "name_asc", true)
    const lastElementRef = useInfiniteScroll(loadingList, list.length, count, pageSize, setPageIndex)

    const tempList = useMemo(() => list.filter(item => !form.values.tags.find(item2 => item2.id === item.id)), [list, form.values.tags])
    const renderList = useMemo(() => tempList.filter(item => item.name.toLowerCase().includes(filter.toLowerCase())), [filter, tempList])
    const selectedItems = useMemo(() => form.values.tags.sort((a, b) => a.name.localeCompare(b.name)), [form.values.tags])

    const isInvalid = typeof form.errors.tags === "string"


    return (
        <>
            <DynamicBox label="Link tags" isInvalid={isInvalid} feedback={form.errors.tags} button="Tag"
                        onClick={() => setShow(true)}>
                {selectedItems.map(item => (
                    <Button key={item.id} className="mb-2" onClick={() => remove(item.id)} variant={item.variant}>{item.name} <i
                        className="bi bi-x-lg"/></Button>
                ))}
            </DynamicBox>
            <DynamicDialog title="Select Tag" show={show} onHide={() => setShow(false)} filter={filter} setFilter={setFilter}>
                <div>Loaded {list.length - selectedItems.length} of {count - selectedItems.length}</div>
                {renderList && renderList.length > 0 ?
                    renderList.map((item, index) => (
                        <ListGroup.Item key={item.id} eventKey={item.id} ref={renderList.length === index + 1 ? lastElementRef : null}>
                            <Button variant={item.variant} onClick={() => push(list.find(c => c.id === item.id))} className="mb-2">{item.name}</Button>
                        </ListGroup.Item>
                    ))
                    :
                    <span className="text-center">No tag found</span>
                }
            </DynamicDialog>
        </>
    )
}

